import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Globe, Mail, Package, User } from 'lucide-react';
import { useAuth } from '../lib/auth';

// Mobile/native primary navigation. Mirrors the desktop sidebar's active-state recipe.
// Rendered only on mobile (md:hidden); on md+ the left sidebar owns navigation. The mobile
// top header's hamburger still opens the full drawer for the long-tail links (Operations,
// Market, Leads, Team, Admin, Sign out) that don't fit five tabs.
export default function MobileTabBar({ unreadCount = 0 }: { unreadCount?: number }) {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();

  const tabs = [
    { to: '/dashboard/home', label: t('tabBar.home', 'Home'), icon: Home },
    { to: '/dashboard/exchange', label: t('tabBar.exchange', 'Exchange'), icon: Globe },
    { to: '/dashboard/messages', label: t('tabBar.messages', 'Messages'), icon: Mail, badge: unreadCount },
    { to: '/dashboard/inventory', label: t('tabBar.inventory', 'Inventory'), icon: Package },
    { to: `/dashboard/profile/${user?.id}`, label: t('tabBar.profile', 'Profile'), icon: User },
  ];

  const isActive = (to: string) => pathname === to || pathname.startsWith(to + '/');

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-white border-t border-stone-100 h-tabbar pb-safe flex"
      aria-label="Primary"
    >
      {tabs.map(({ to, label, icon: Icon, badge }) => {
        const active = isActive(to);
        return (
          <Link
            key={to}
            to={to}
            aria-current={active ? 'page' : undefined}
            className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium transition-colors ${
              active ? 'text-stone-950' : 'text-stone-400 hover:text-stone-600'
            }`}
          >
            <span className="relative">
              <Icon size={20} strokeWidth={active ? 2.25 : 2} />
              {!!badge && badge > 0 && (
                <span className="absolute -top-1.5 -right-2 bg-accent text-white text-[9px] font-bold min-w-[15px] h-[15px] rounded-full flex items-center justify-center px-0.5">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </span>
            <span className="leading-none">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}
